(() => {
  if (customElements.get('edvi-set-configurator')) return;
  const categoryKeys = ['president', 'royale', 'majestic', 'women'];
  const categoryAliases = {president:'president',royale:'royale',majestic:'majestic',women:'women'};

  class EdviSetConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.infinite = this.hasAttribute('data-config-infinite');
      this.revision = (this.revision || 0) + 1;
      this.request = null;
      this.cache = new Map();
      this.form = this.querySelector('[data-config-form]');
      this.controls = this.querySelector('[data-config-controls]');
      this.status = this.querySelector('[data-config-status]');
      this.results = this.querySelector('[data-final-set-results]');
      this.mobile = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.config = JSON.parse(this.closest('[data-final-set-root]').querySelector('[data-final-set-config]').textContent);
      this.excluded = new Set([...(this.config.excludedHandles || []), 'opening-set-3', 'opening-set-6', ...Object.values(this.config.gifts || {}).map(gift => gift.handle)]);
      this.currency = new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency });
      this.desiredURL = this.cleanURL(new URL(location.href));
      this.renderedURL = '';
      this.visibleCount = 24;
      this.form.noValidate = true;
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('input', event => this.onInput(event), options);
      this.addEventListener('submit', event => { if (event.target === this.form) { event.preventDefault(); this.apply(); } }, options);
      document.addEventListener('keydown', event => this.onKeydown(event), options);
      document.addEventListener('pointerdown', event => {
        if (this.panel && !this.panel.contains(event.target) && !event.target.closest('[data-config-toggle]')) this.closePanel(this.mobile.matches);
      }, options);
      document.addEventListener('focusin', event => {
        if (this.panel && !this.mobile.matches && !this.panel.contains(event.target) && event.target !== this.panelTrigger) this.closePanel(false);
      }, options);
      addEventListener('resize', () => this.positionPanel(), options);
      addEventListener('scroll', () => {
        if (!this.panel || this.mobile.matches || this.positionFrame) return;
        this.positionFrame = requestAnimationFrame(() => { this.positionFrame = 0; this.positionPanel(); });
      }, { ...options, passive: true });
      addEventListener('popstate', () => { this.closePanel(false); this.load(new URL(location.href), { history: false }); }, options);
      this.mobile.addEventListener('change', () => this.closePanel(), options);
      this.prepareControls();
      this.syncButtons();
      if (this.infinite) {
        this.snapshot = this.readPage(this.querySelector('[data-config-snapshot]'));
        this.renderedURL = this.desiredURL.href;
        this.paginationPaused = false;
        this.pageFailed = false;
        this.pageObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) void this.more();
        }, { rootMargin: '0px 0px 600px 0px' });
        if (this.categories().length) void this.load(this.desiredURL, { history: false });
        else this.syncPagination();
        return;
      }
      const pager = this.querySelector('[data-final-set-pagination]');
      if (pager) {
        pager.querySelector('[data-final-set-page-links]').hidden = true;
        const more = pager.querySelector('[data-final-set-load-more]');
        more.hidden = Number(pager.dataset.nextPage) === 0;
        more.textContent = pager.dataset.more;
      }
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        if (!this.snapshot && !this.request) this.load(this.desiredURL, { history: false, warm: true });
      }, { rootMargin: '400px' });
      this.observer.observe(this);
      if (this.categories().length) this.load(this.desiredURL, { history: false });
    }

    disconnectedCallback() {
      this.closePanel(false);
      this.events?.abort();
      this.request?.abort();
      this.observer?.disconnect();
      this.pageObserver?.disconnect();
      this.revision += 1;
      this.request = null;
      clearTimeout(this.inputTimer);
      cancelAnimationFrame(this.positionFrame);
      this.events = null;
    }

    cleanURL(value) {
      const url = new URL(value, location.href);
      if (url.origin !== location.origin) throw new Error('CONFIG_ORIGIN');
      url.pathname = this.dataset.collectionUrl;
      for (const key of [...url.searchParams.keys()]) if (/^page(?:_|$)/.test(key) || ['section_id', 'sections'].includes(key)) url.searchParams.delete(key);
      // Keep the template that supplied this section ID (default or final-set).
      return url;
    }

    categories(url = this.desiredURL) {
      return [...new Set(url.searchParams.getAll('set_category'))].filter(key => categoryKeys.includes(key));
    }

    snapshotKey(url) {
      const key = this.cleanURL(url);
      for (const name of ['set_category', 'preselect']) key.searchParams.delete(name);
      key.hash = '';
      key.searchParams.sort();
      return key.href;
    }

    prepareControls() {
      this.querySelectorAll('[data-facet-group]').forEach(group => { group.open = true; });
      this.querySelectorAll('[data-config-category]').forEach(button => { button.dataset.label = button.childNodes[0].textContent.trim(); });
      this.syncPrices();
    }

    syncButtons() {
      const selected = this.categories();
      const metals = this.desiredURL.searchParams.getAll('filter.p.m.custom.color');
      this.querySelectorAll('[data-config-metal]').forEach(button => button.setAttribute('aria-pressed', String(metals.includes(button.dataset.configMetal))));
      this.querySelectorAll('[data-config-category]').forEach(button => button.setAttribute('aria-pressed', String(selected.includes(button.dataset.configCategory))));
      const hasFilter = [...this.desiredURL.searchParams.keys()].some(key => key.startsWith('filter.') || key === 'set_category');
      this.querySelector('[data-config-clear]').hidden = !hasFilter;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const metal = event.target.closest('[data-config-metal]');
      const category = event.target.closest('[data-config-category]');
      const toggle = event.target.closest('[data-config-toggle]');
      if (toggle) { this.panelTrigger === toggle ? this.closePanel() : this.openPanel(toggle); return; }
      if (event.target.closest('[data-config-close], [data-config-backdrop]')) { this.closePanel(); return; }
      if (metal || category) {
        clearTimeout(this.inputTimer);
        const url = new URL(this.desiredURL);
        if (metal) {
          const key = metal.dataset.param;
          const wasActive = url.searchParams.getAll(key).includes(metal.dataset.configMetal);
          url.searchParams.delete(key);
          if (!wasActive) url.searchParams.set(key, metal.dataset.configMetal);
        } else {
          const values = new Set(this.categories(url));
          values.has(category.dataset.configCategory) ? values.delete(category.dataset.configCategory) : values.add(category.dataset.configCategory);
          url.searchParams.delete('set_category');
          values.forEach(value => url.searchParams.append('set_category', value));
        }
        this.load(url);
        return;
      }
      if (event.target.closest('[data-config-clear]')) {
        const url = new URL(this.desiredURL);
        for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'set_category') url.searchParams.delete(key);
        this.closePanel(false);
        this.load(url);
        return;
      }
      const reset = event.target.closest('[data-config-reset]');
      if (reset) {
        const url = new URL(this.desiredURL);
        for (const key of [...url.searchParams.keys()]) if (key === reset.dataset.configReset || key.startsWith(`${reset.dataset.configReset}.`)) url.searchParams.delete(key);
        this.load(url);
        return;
      }
      if (event.target.closest('[data-config-retry]')) { this.load(this.desiredURL); return; }
      if (event.target.closest('[data-final-set-load-more], [data-final-set-load-retry]')) {
        event.preventDefault();
        this.more();
        return;
      }
      const link = event.target.closest('[data-facet-link], [data-facet-pagination] a');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        if (link.closest('[data-facet-pagination]')) { this.more(); return; }
        const url = new URL(this.desiredURL);
        for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'set_category') url.searchParams.delete(key);
        this.load(url);
      }
    }

    onChange(event) {
      if (!(event.target instanceof HTMLInputElement) || !this.form.contains(event.target)) return;
      clearTimeout(this.inputTimer);
      const price = event.target.closest('[data-facet-price]');
      if (price) this.syncPrice(price, event.target);
      this.apply();
    }

    onInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.closest('[data-facet-price]')) return;
      this.request?.abort();
      this.revision += 1;
      this.request = null;
      this.setBusy(false);
      if (this.infinite) {
        this.paginationPaused = true;
        this.pageObserver.disconnect();
        this.setPageBusy(false);
      }
      clearTimeout(this.inputTimer);
      this.syncPrice(input.closest('[data-facet-price]'), input);
      if (input.type !== 'range') this.inputTimer = setTimeout(() => this.apply(), 450);
    }

    apply() {
      const pricesValid = this.syncPrices();
      const formValid = this.form.reportValidity();
      if (!pricesValid || !formValid) {
        const invalid = this.form.querySelector('[aria-invalid="true"]');
        invalid?.focus({ preventScroll: true });
        invalid?.closest('[data-facet-price]')?.querySelector('[data-price-error]')?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        return;
      }
      const url = new URL(this.desiredURL);
      const names = new Set([...this.form.elements].map(input => input.name).filter(name => name?.startsWith('filter.') && name !== 'filter.p.m.custom.color'));
      names.forEach(name => url.searchParams.delete(name));
      for (const [name, value] of new FormData(this.form)) if (names.has(name) && String(value).trim()) url.searchParams.append(name, value);
      this.load(url);
    }

    syncPrices() { return [...this.querySelectorAll('[data-facet-price]')].map(price => this.syncPrice(price)).every(Boolean); }

    syncPrice(price, changed) {
      const limit = Number(price.dataset.max);
      const min = price.querySelector('[data-price-min]'), max = price.querySelector('[data-price-max]');
      const low = price.querySelector('[data-range-min]'), high = price.querySelector('[data-range-max]');
      let from = min.value === '' ? 0 : Number(min.value), to = max.value === '' ? limit : Number(max.value);
      const clamp = value => Math.max(0, Math.min(limit, Number.isFinite(value) ? value : 0));
      if (changed === low) { from = Math.min(clamp(Number(low.value)), clamp(to)); min.value = String(from); }
      if (changed === high) { to = Math.max(clamp(Number(high.value)), clamp(from)); max.value = String(to); }
      const bounds = price.dataset.boundsError.replace('[min]', this.currency.format(0)).replace('[max]', this.currency.format(limit));
      const invalid = (field, value) => field.validity.badInput || !Number.isInteger(value) || value < 0 || value > limit;
      const minError = from > to ? price.dataset.orderError : invalid(min, from) ? bounds : '';
      const maxError = invalid(max, to) ? bounds : '';
      min.setCustomValidity(minError); max.setCustomValidity(maxError);
      min.setAttribute('aria-invalid', String(Boolean(minError))); max.setAttribute('aria-invalid', String(Boolean(maxError)));
      const error = price.querySelector('[data-price-error]');
      error.setAttribute('role', 'alert');
      error.setAttribute('aria-atomic', 'true');
      error.textContent = minError || maxError;
      error.hidden = !error.textContent;
      low.value = clamp(from); high.value = Math.max(clamp(from), clamp(to));
      low.setAttribute('aria-valuemax', high.value); high.setAttribute('aria-valuemin', low.value);
      low.setAttribute('aria-valuetext', this.currency.format(Number(low.value))); high.setAttribute('aria-valuetext', this.currency.format(Number(high.value)));
      const track = price.querySelector('.edvi-facets__slider');
      track.style.setProperty('--range-start', `${limit ? Number(low.value) / limit * 100 : 0}%`);
      track.style.setProperty('--range-end', `${limit ? Number(high.value) / limit * 100 : 100}%`);
      return !error.textContent;
    }

    openPanel(trigger) {
      this.closePanel(false);
      this.panelTrigger = trigger;
      this.panel = this.querySelector(`[data-config-panel="${CSS.escape(trigger.dataset.configToggle)}"]`);
      if (!this.panel) return;
      trigger.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
      this.querySelector('[data-config-bar]').classList.add('has-panel');
      if (this.mobile.matches) {
        this.panel.setAttribute('role', 'dialog'); this.panel.setAttribute('aria-modal', 'true');
        this.previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
        this.querySelector('[data-config-backdrop]').hidden = false;
        this.panel.querySelector('[data-config-close]').focus({ preventScroll: true });
      }
      this.positionPanel();
    }

    closePanel(restore = true) {
      if (!this.panel) return;
      this.panel.hidden = true; this.panel.setAttribute('role', 'region'); this.panel.removeAttribute('aria-modal');
      this.panelTrigger?.setAttribute('aria-expanded', 'false');
      this.querySelector('[data-config-backdrop]').hidden = true;
      this.querySelector('[data-config-bar]').classList.remove('has-panel');
      if (this.previousOverflow !== undefined) { document.body.style.overflow = this.previousOverflow; this.previousOverflow = undefined; }
      if (restore && this.panelTrigger?.isConnected) this.panelTrigger.focus({ preventScroll: true });
      this.panel = null; this.panelTrigger = null;
    }

    positionPanel() {
      if (!this.panel || this.mobile.matches) return;
      const rect = this.panelTrigger.getBoundingClientRect();
      const width = Math.min(336, innerWidth - 32);
      this.panel.style.setProperty('--panel-left', `${Math.min(Math.max(16, rect.right - width), innerWidth - width - 16)}px`);
      this.panel.style.setProperty('--panel-top', `${rect.bottom + 12}px`);
      this.panel.style.setProperty('--panel-room', `${Math.max(160, innerHeight - rect.bottom - 32)}px`);
    }

    onKeydown(event) {
      if (event.key === 'Enter' && this.form.contains(event.target) && event.target.closest('[data-facet-price]')) {
        event.preventDefault();
        clearTimeout(this.inputTimer);
        this.apply();
        return;
      }
      if (event.key === 'Escape' && this.panel) { event.preventDefault(); this.closePanel(); return; }
      if (event.key !== 'Tab' || !this.panel || !this.mobile.matches) return;
      const items = [...this.panel.querySelectorAll('button, input, a[href]')].filter(item => !item.disabled && item.getClientRects().length);
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !this.panel.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
    }

    setBusy(busy, warm = false) {
      this.results.setAttribute('aria-busy', String(busy && !warm));
      this.classList.toggle('is-loading', busy && !warm);
      this.querySelectorAll('[data-final-set-load-more], [data-final-set-load-retry]').forEach(button => { button.disabled = busy; });
    }

    async fetchPage(url, signal, page = 1, pageParam = 'page') {
      const requestURL = new URL(url);
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      requestURL.searchParams.set(pageParam, String(page));
      requestURL.searchParams.delete('set_category');
      requestURL.searchParams.delete('preselect');
      const html = this.infinite ? await this.fetchPageHTML(requestURL, signal) : await (async () => {
        const response = await fetch(requestURL, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`CATALOG_${response.status}`);
        return response.text();
      })();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const snapshot = doc.querySelector('[data-config-snapshot]');
      if (!snapshot || snapshot.dataset.handle !== this.config.collectionHandle || snapshot.dataset.currency !== this.config.currency || snapshot.dataset.locale !== this.config.locale || Number(snapshot.dataset.page) !== page) throw new Error('CATALOG_CONTEXT');
      return snapshot;
    }

    async fetchSnapshot(url, signal) {
      const first = await this.fetchPage(url, signal);
      if (this.infinite) {
        const snapshot = this.readPage(first);
        await this.fillPage(snapshot, url, signal, 24);
        return snapshot;
      }
      const pageCount = Math.max(1, Number(first.dataset.pages));
      const pages = [first];
      for (let page = 2; page <= pageCount; page += 2) {
        const pending = [this.fetchPage(url, signal, page, first.dataset.pageParam)];
        if (page + 1 <= pageCount) pending.push(this.fetchPage(url, signal, page + 1, first.dataset.pageParam));
        pages.push(...await Promise.all(pending));
      }
      const seen = new Set(), items = [];
      for (const page of pages) {
        if (page.dataset.total !== first.dataset.total) throw new Error('CATALOG_CHANGED');
        for (const wrapper of page.querySelector('[data-config-cards]').content.children) {
          if (seen.has(wrapper.dataset.id) || this.excluded.has(wrapper.dataset.handle)) continue;
          seen.add(wrapper.dataset.id);
          const tags = JSON.parse(wrapper.dataset.tags || '[]');
          let categories = tags.filter(tag => tag.startsWith('edvi-category:')).map(tag => categoryAliases[tag.slice(14).toLowerCase()]).filter(Boolean);
          if (!categories.length) categories = [categoryAliases[wrapper.dataset.type.toLowerCase().trim()]].filter(Boolean);
          const card = wrapper.querySelector('[data-final-set-product]');
          if (card) items.push({ id: wrapper.dataset.id, categories, card });
        }
      }
      return { items, controls: first.querySelector('[data-config-controls]').content.cloneNode(true) };
    }

    async load(value, options = {}) {
      clearTimeout(this.inputTimer);
      const url = this.cleanURL(value);
      for (const name of ['preview_theme_id', '_fd', 'pb', 'country', 'currency']) {
        const current = new URL(location.href);
        if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
      }
      const currentPreselect = new URL(location.href).searchParams.get('preselect');
      currentPreselect ? url.searchParams.set('preselect', currentPreselect) : url.searchParams.delete('preselect');
      this.desiredURL = url;
      this.syncButtons();
      this.request?.abort();
      if (this.infinite) {
        this.paginationPaused = true;
        this.pageFailed = false;
        this.pageObserver.disconnect();
        this.setPageBusy(false);
      }
      const controller = new AbortController(); this.request = controller;
      const revision = ++this.revision;
      this.setBusy(true, options.warm);
      this.querySelector('[data-config-notice]').hidden = true;
      this.dispatchEvent(new CustomEvent('edvi:collection-loading', { bubbles: true, detail: { url: url.href } }));
      if (!options.warm) this.status.textContent = this.dataset.loadingLabel;
      const timeout = setTimeout(() => controller.abort('timeout'), 30000);
      try {
        const key = this.snapshotKey(url);
        const snapshot = (!this.infinite && this.cache.get(key)) || await this.fetchSnapshot(url, controller.signal);
        if (revision !== this.revision || !this.isConnected) return false;
        if (!this.infinite) this.cache.set(key, snapshot);
        if (this.cache.size > 2) this.cache.delete(this.cache.keys().next().value);
        this.snapshot = snapshot;
        this.replaceControls(snapshot.controls);
        const latestPreselect = new URL(location.href).searchParams.get('preselect');
        latestPreselect ? url.searchParams.set('preselect', latestPreselect) : url.searchParams.delete('preselect');
        this.desiredURL = url;
        this.renderedURL = url.href;
        this.visibleCount = 24;
        this.paginationPaused = false;
        this.renderProducts();
        if (options.history !== false && location.href !== url.href) history.pushState({ ...history.state, edviSetConfigurator: true }, '', url);
        this.syncButtons();
        this.status.textContent = this.resultCount;
        return true;
      } catch (error) {
        if (revision !== this.revision || (controller.signal.aborted && controller.signal.reason !== 'timeout')) return false;
        this.querySelector('[data-config-notice]').hidden = false;
        this.status.textContent = this.dataset.errorLabel;
        this.dispatchEvent(new CustomEvent('edvi:collection-error', { bubbles: true }));
        return false;
      } finally {
        clearTimeout(timeout);
        if (revision === this.revision) { this.request = null; this.setBusy(false); if (this.infinite) this.syncPagination(); }
      }
    }

    replaceControls(fragment) {
      const focus = document.activeElement;
      const identity = { id: focus?.id, name: focus?.name, value: focus?.value, category: focus?.dataset.configCategory, metal: focus?.dataset.configMetal, toggle: focus?.dataset.configToggle };
      const open = this.panelTrigger?.dataset.configToggle;
      const panelScroll = this.panel?.querySelector('.edvi-set-config__panel-body').scrollTop || 0;
      const railScroll = this.querySelector('[data-config-rail]').scrollLeft;
      const refineScroll = this.querySelector('.edvi-set-config__refine')?.scrollLeft || 0;
      const restore = this.controls.contains(focus);
      this.closePanel(false);
      this.controls.replaceChildren(fragment.cloneNode(true));
      this.prepareControls();
      this.querySelector('[data-config-rail]').scrollLeft = railScroll;
      const refine = this.querySelector('.edvi-set-config__refine');
      if (refine) refine.scrollLeft = refineScroll;
      if (open) {
        const trigger = this.querySelector(`[data-config-toggle="${CSS.escape(open)}"]`);
        if (trigger) { this.openPanel(trigger); this.panel.querySelector('.edvi-set-config__panel-body').scrollTop = panelScroll; }
      }
      if (restore) {
        const replacement = [...this.controls.querySelectorAll('button, input')].find(item =>
          identity.name ? item.name === identity.name && (item.type !== 'checkbox' || item.value === identity.value) :
          identity.category ? item.dataset.configCategory === identity.category :
          identity.metal ? item.dataset.configMetal === identity.metal :
          identity.toggle ? item.dataset.configToggle === identity.toggle : identity.id && item.id === identity.id);
        (replacement && !replacement.disabled ? replacement : this.panelTrigger || this.querySelector('[data-config-metal]'))?.focus({ preventScroll: true });
      }
    }

    renderProducts(append = false) {
      const categories = this.categories();
      const items = this.snapshot.items.filter(item => !categories.length || item.categories.some(category => categories.includes(category)));
      const grid = this.querySelector('[data-final-set-grid]');
      const focused = document.activeElement;
      const focusedCard = focused?.closest('[data-final-set-product]');
      const focusID = focusedCard?.dataset.productId;
      const focusControl = focused?.matches('[data-final-set-select]') ? '[data-final-set-select]' : focused?.matches('[data-final-set-variant]') ? '[data-final-set-variant]' : 'a';
      const variants = new Map([...grid.children].map(card => [card.dataset.productId, card.querySelector('[data-final-set-variant]')?.value]));
      const start = append ? grid.children.length : 0;
      const nodes = items.slice(start, this.visibleCount).map(item => item.card.cloneNode(true));
      nodes.forEach(card => {
        const value = variants.get(card.dataset.productId);
        if (value) card.querySelector('[data-final-set-variant]').value = value;
      });
      append ? grid.append(...nodes) : grid.replaceChildren(...nodes);
      categoryKeys.forEach(category => {
        const count = this.snapshot.items.filter(item => item.categories.includes(category)).length;
        const button = this.querySelector(`[data-config-category="${category}"]`);
        if (!button) return;
        const exact = !this.infinite || !this.snapshot.nextURL;
        button.querySelector('[data-config-category-count]').textContent = exact ? String(count) : '';
        button.setAttribute('aria-label', exact ? `${button.dataset.label}, ${count}` : button.dataset.label);
      });
      this.resultCount = this.dataset.countLabel.replace('[count]', items.length);
      const empty = this.querySelector('[data-config-empty]'); empty.hidden = Boolean(items.length || (this.infinite && this.snapshot.nextURL));
      this.querySelector('[data-final-set-empty]')?.remove();
      const pager = this.querySelector('[data-final-set-pagination]');
      if (this.infinite) this.syncPagination();
      else {
      pager.querySelector('[data-final-set-page-links]').hidden = true;
      pager.querySelector('[data-final-set-load-retry]').hidden = true;
      const more = pager.querySelector('[data-final-set-load-more]');
      more.hidden = items.length <= this.visibleCount;
      more.disabled = false;
      more.textContent = pager.dataset.more;
      pager.querySelector('[data-final-set-load-status]').textContent = '';
      }
      this.dispatchEvent(new CustomEvent('edvi:collection-updated', { bubbles: true, detail: { url: this.desiredURL.href, sectionId: this.dataset.sectionId } }));
      if (focusID && !focused.isConnected) {
        const replacement = [...grid.children].find(card => card.dataset.productId === focusID)?.querySelector(focusControl);
        (replacement && !replacement.disabled ? replacement : this.results).focus({ preventScroll: true });
      }
    }

    async more() {
      if (this.infinite) { await this.loadNextPage(); return; }
      if (this.request) return;
      if (!this.snapshot && !await this.load(this.desiredURL, { history: false })) return;
      const grid = this.querySelector('[data-final-set-grid]');
      const before = grid.children.length;
      this.visibleCount += 24;
      this.renderProducts(true);
      const next = grid.children[before];
      if (next) { next.tabIndex = -1; next.focus({ preventScroll: true }); }
    }

    readPage(source) {
      const items = [], seen = new Set();
      for (const wrapper of source.querySelector('[data-config-cards]').content.children) {
        if (seen.has(wrapper.dataset.id) || this.excluded.has(wrapper.dataset.handle)) continue;
        seen.add(wrapper.dataset.id);
        const tags = JSON.parse(wrapper.dataset.tags || '[]');
        let categories = tags.filter(tag => tag.startsWith('edvi-category:')).map(tag => categoryAliases[tag.slice(14).toLowerCase()]).filter(Boolean);
        if (!categories.length) categories = [categoryAliases[wrapper.dataset.type.toLowerCase().trim()]].filter(Boolean);
        const card = wrapper.querySelector('[data-final-set-product]');
        if (card) items.push({ id: wrapper.dataset.id, categories, card });
      }
      return { items, controls: source.querySelector('[data-config-controls]').content.cloneNode(true), page: Number(source.dataset.page), pageParam: source.dataset.pageParam || 'page', nextURL: source.dataset.nextUrl || '', total: source.dataset.total };
    }

    matchingItems(snapshot = this.snapshot, url = this.desiredURL) {
      const categories = this.categories(url);
      return snapshot.items.filter(item => !categories.length || item.categories.some(category => categories.includes(category)));
    }

    async fetchPageHTML(url, signal) {
      if (url.origin !== location.origin || url.pathname !== this.dataset.collectionUrl) throw new Error('CATALOG_URL');
      for (let attempt = 0; attempt < 2; attempt += 1) {
        signal.throwIfAborted();
        const controller = new AbortController();
        const abort = () => controller.abort(signal.reason);
        signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(() => controller.abort('timeout'), 12000);
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          const returned = new URL(response.url || url);
          if (!response.ok || returned.origin !== url.origin || returned.pathname !== url.pathname) throw new Error(`CATALOG_${response.status}`);
          return await response.text();
        } catch (error) {
          if (signal.aborted || attempt === 1) throw error;
        } finally {
          clearTimeout(timeout);
          signal.removeEventListener('abort', abort);
        }
        await new Promise((resolve, reject) => {
          const cancel = () => { clearTimeout(timer); reject(signal.reason); };
          const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 800);
          signal.addEventListener('abort', cancel, { once: true });
          if (signal.aborted) cancel();
        });
      }
    }

    async fillPage(snapshot, url, signal, limit) {
      while (snapshot.nextURL && this.matchingItems(snapshot, url).length < limit) {
        signal.throwIfAborted();
        const nextURL = new URL(snapshot.nextURL, url);
        const page = Number(nextURL.searchParams.get(snapshot.pageParam));
        if (!Number.isInteger(page) || page <= snapshot.page) throw new Error('CATALOG_PAGE');
        const next = this.readPage(await this.fetchPage(nextURL, signal, page, snapshot.pageParam));
        if (next.total !== snapshot.total) throw new Error('CATALOG_CHANGED');
        const seen = new Set(snapshot.items.map(item => item.id));
        snapshot.items.push(...next.items.filter(item => !seen.has(item.id)));
        snapshot.page = next.page;
        snapshot.nextURL = next.nextURL;
      }
    }

    setPageBusy(busy) {
      this.querySelector('[data-config-loader]').hidden = !busy;
      this.querySelector('[data-final-set-grid]').setAttribute('aria-busy', String(busy));
      const pager = this.querySelector('[data-final-set-pagination]');
      pager.querySelector('[data-final-set-load-status]').textContent = busy ? pager.dataset.loading : '';
    }

    syncPagination() {
      if (!this.infinite || !this.snapshot) return;
      const pager = this.querySelector('[data-final-set-pagination]');
      pager.dataset.currentPage = String(this.snapshot.page);
      pager.dataset.nextPage = this.snapshot.nextURL ? String(this.snapshot.page + 1) : '0';
      this.pageObserver.disconnect();
      const hasMore = this.snapshot.nextURL || this.matchingItems().length > this.visibleCount;
      if (hasMore && !this.request && !this.paginationPaused && !this.pageFailed && this.isConnected) this.pageObserver.observe(this.querySelector('[data-config-sentinel]'));
    }

    async loadNextPage() {
      if (this.request || this.paginationPaused || this.pageFailed || !this.isConnected || !this.snapshot) return;
      if (!this.snapshot.nextURL && this.matchingItems().length <= this.visibleCount) return;
      const revision = this.revision;
      const controller = new AbortController();
      this.request = controller;
      this.pageObserver.disconnect();
      this.setPageBusy(true);
      const snapshot = { ...this.snapshot, items: [...this.snapshot.items] };
      const limit = this.visibleCount + 24;
      try {
        await this.fillPage(snapshot, this.desiredURL, controller.signal, limit);
        if (controller.signal.aborted || revision !== this.revision || !this.isConnected) return;
        this.snapshot = snapshot;
        this.visibleCount = limit;
        this.renderProducts(true);
      } catch (error) {
        if (revision === this.revision && !controller.signal.aborted) this.pageFailed = true;
      } finally {
        if (revision === this.revision) {
          this.request = null;
          this.setPageBusy(false);
          this.syncPagination();
        }
      }
    }
  }
  customElements.define('edvi-set-configurator', EdviSetConfigurator);
})();
